import { useCallback, useEffect, useMemo, useState } from 'react';
import { reportsApi, downloadExport, type ExportItem, type ExportOptions, type ExportParams } from '../lib/reportsApi';
import { Link } from 'react-router-dom';

const startOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

/**
 * What is actually IN each file, one line, in the order the columns appear.
 *
 * Keyed by kind and used only as copy — the LIST of reports still comes from the server. Somebody
 * choosing between "Student attendance" and "Exam results" should not have to download both to
 * find out which one has the marks in it.
 *
 * ⚠ An unknown kind falls back to a neutral line rather than rendering blank, so a report added on
 * the server appears here the day it ships instead of waiting for this map.
 */
const COLUMNS: Record<string, string> = {
  Students: 'Admission number, name, class, guardian, mobile, email, date enrolled, status.',
  Staff: 'Employee code, name, designation, department, joining date, mobile, status.',
  Attendance: 'One row per student per day — date, admission number, name, class, present or absent.',
  StaffAttendance: 'One row per employee per day — date, code, name, status, in and out times, how it was marked.',
  ExamResults: 'Admission number, name, class, subject, marks (AB where absent) and grade.',
  FeeDefaulters: 'Unpaid invoices — student, class, invoice number, due date, total, paid, balance, days overdue, guardian mobile.',
  Certificates: 'Serial number, type, student, status and the date it was issued.',
};

const describe = (kind: string) => COLUMNS[kind] ?? 'A spreadsheet of this data, straight from your school’s records.';

/**
 * FILTER COPY — the label above each dropdown, and what the empty choice reads as.
 *
 * ⚠ COPY ONLY. Which filters a report has, and what may go in them, both come from the server; this
 * map cannot add a filter or a value. An unrecognised key still renders, titled with the key
 * itself — so a filter added on the server works the day it ships and merely looks unpolished
 * until a line is added here, rather than silently not appearing.
 */
const FILTER_LABEL: Record<string, string> = {
  class: 'Class',
  department: 'Department',
  status: 'Students to include',
  staffStatus: 'Staff to include',
  exam: 'Exam',
  certificateType: 'Certificate type',
};

/**
 * The "no filter" line, for the filters whose lists are pure data.
 *
 * ⚠ status, staffStatus and certificateType are NOT here on purpose: the server sends their empty
 * choice as the first entry, worded for what it actually does ("Active students" — not "All", which
 * would be a lie about a roster). Adding a second empty line here would give those two.
 */
const FILTER_ANY: Record<string, string> = {
  class: 'All classes',
  department: 'All departments',
  exam: 'All exams',
};

/**
 * Filter key → the query parameter the export reads.
 *
 * ⚠ status and staffStatus BOTH send `status`. One export takes a student status and the other an
 * employee status; they are separate controls because they offer different words, and one parameter
 * because no export reads both.
 */
const PARAM_OF: Record<string, keyof ExportParams> = {
  class: 'academicClassId',
  department: 'departmentId',
  status: 'status',
  staffStatus: 'status',
  exam: 'examName',
  certificateType: 'certificateType',
};

const prettyDate = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const EMPTY_OPTIONS: ExportOptions = {
  class: [], department: [], exam: [], status: [], staffStatus: [], certificateType: [],
};

/** Reports: the CSV exports this member of staff is allowed to pull for their own school. */
export function ReportsPage() {
  const [items, setItems] = useState<ExportItem[] | null>(null);
  const [options, setOptions] = useState<ExportOptions>(EMPTY_OPTIONS);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [from, setFrom] = useState(startOfMonth());
  const [to, setTo] = useState(today());
  // kind → filter key → chosen value. Per kind, because two reports on this page can legitimately
  // be narrowed to different classes and sharing one selection between them would be surprising.
  const [picked, setPicked] = useState<Record<string, Record<string, string>>>({});
  // ⚠ ONE download at a time, tracked by kind. A big export takes seconds, and a button that looks
  // inert gets pressed four times — four requests, four identical files in the Downloads folder.
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<Record<string, string>>({});

  useEffect(() => {
    reportsApi.catalogue()
      .then(setItems)
      .catch(() => { setItems([]); setLoadError('Could not load the list of reports. Try again in a moment.'); });

    // ⚠ Failing to load the filter lists must NOT take the page down with it. Every filter here is
    // optional; without them the downloads still work and give the whole school, which is what this
    // page did before filters existed. A blank Reports page because one extra call 500'd would be a
    // far worse outcome than an unfiltered file.
    reportsApi.options()
      .then(setOptions)
      .catch(() => setOptions(EMPTY_OPTIONS));
  }, []);

  const needsDatesAnywhere = !!items?.some((i) => i.needsDates);

  const rangeLabel = useMemo(() => `${prettyDate(from)} – ${prettyDate(to)}`, [from, to]);

  const set = useCallback((kind: string, key: string, value: string) => {
    setPicked((p) => ({ ...p, [kind]: { ...(p[kind] ?? {}), [key]: value } }));
  }, []);

  const run = useCallback(async (item: ExportItem): Promise<void> => {
    setBusy(item.kind);
    setErrors((e) => ({ ...e, [item.kind]: '' }));
    setSaved((s) => ({ ...s, [item.kind]: '' }));

    const chosen = picked[item.kind] ?? {};
    const params: ExportParams = {
      from: item.needsDates ? from : undefined,
      to: item.needsDates ? to : undefined,
    };
    for (const key of item.filters) {
      const value = chosen[key];
      const param = PARAM_OF[key];
      if (value && param) params[param] = value;
    }

    try {
      const name = await downloadExport(item.kind, params);
      setSaved((s) => ({ ...s, [item.kind]: name }));
    } catch (err) {
      setErrors((e) => ({ ...e, [item.kind]: err instanceof Error ? err.message : 'The download failed.' }));
    } finally {
      setBusy(null);
    }
  }, [from, to, picked]);

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Exports</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Reports</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: 640 }}>
          Your school’s own records, as a spreadsheet. Every report downloads as a <strong>CSV file</strong>,
          which opens in Excel — or in Google Sheets, or LibreOffice. Narrow a report before you
          download it — a class, a department, one exam. Leave the filters alone and you get
          everything you are allowed to see.
        </p>
        {/* ⚠ The register is a different SHAPE of the same attendance, and people arriving here
            looking for "the attendance sheet" mean that one. Saying so beats them downloading a
            60,000-row CSV first and finding out. */}
        <p style={{ color: 'var(--ink-3)', fontSize: 12.5, marginTop: 6, maxWidth: 640 }}>
          Looking for the month on one sheet, with a column per day to sign and file?
          That is the <Link className="link" to="/reports/register">attendance register</Link>.
        </p>
      </div>

      {items === null && (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</div>
      )}

      {loadError && (
        <div className="card" style={{ padding: 18, color: 'var(--danger)' }}>{loadError}</div>
      )}

      {/* ⚠ Say so plainly. A role with no exports rendering as an empty page reads as a broken screen. */}
      {items !== null && items.length === 0 && !loadError && (
        <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--ink-2)' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No reports for your role</div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
            Downloads are granted per report. Ask your school administrator if you need one.
          </div>
        </div>
      )}

      {needsDatesAnywhere && (
        <div className="card" style={{ padding: 16, marginBottom: 14, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label className="label">From</label>
            <input className="input" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={{ width: 165 }} />
          </div>
          <div>
            <label className="label">To</label>
            <input className="input" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={{ width: 165 }} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', paddingBottom: 9, flex: '1 1 200px' }}>
            Applies to the reports marked with a date range. Defaults to this month.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {(items ?? []).map((item) => {
          const chosen = picked[item.kind] ?? {};

          // ⚠ A filter with no choices is not rendered. The server sends an empty list when this
          // caller may not use it, or when the school has no data for it — an empty dropdown is a
          // control that looks broken, and a disabled one invites a support call.
          const shown = item.filters.filter((key) => (options[key as keyof ExportOptions] ?? []).length > 0);

          // A teacher must pick a class. Blocked rather than sent, because the 400 that comes back
          // is one they can do nothing about until they choose.
          const blocked = item.needsClass && !chosen.class;
          const isBusy = busy === item.kind;

          return (
            <div key={item.kind} className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {item.label}
                    <span className="pill pill-muted" style={{ fontSize: 10 }}>CSV</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 4, lineHeight: 1.45 }}>
                    {describe(item.kind)}
                  </div>
                  {item.needsDates && (
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>{rangeLabel}</div>
                  )}
                </div>

                {shown.map((key) => {
                  const choices = options[key as keyof ExportOptions] ?? [];
                  const required = key === 'class' && item.needsClass;
                  return (
                    <div key={key} style={{ flex: '1 1 180px', minWidth: 0, maxWidth: 240 }}>
                      <label className="label">{FILTER_LABEL[key] ?? key}</label>
                      <select
                        className="input"
                        value={chosen[key] ?? ''}
                        onChange={(e) => set(item.kind, key, e.target.value)}
                        style={{ width: '100%' }}
                      >
                        {/* ⚠ Only when the server did not already send one. status and
                            certificateType arrive with their own empty first choice, worded for
                            what it does; a second one here would give the person two. */}
                        {!choices.some((c) => c.value === '') && (
                          <option value="">
                            {required ? 'Choose a class' : FILTER_ANY[key] ?? 'Any'}
                          </option>
                        )}
                        {choices.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.count === null || c.count === undefined ? c.label : `${c.label} · ${c.count}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}

                {/* ⚠ A fixed width. "Preparing…" is wider than "Download", and without it the button
                    — and on a phone the whole row — jumps at the moment of the click. */}
                <button
                  className="btn btn-primary"
                  style={{ flex: '0 0 auto', minWidth: 122, justifyContent: 'center', alignSelf: shown.length ? 'flex-end' : 'center' }}
                  disabled={busy !== null || blocked}
                  onClick={() => run(item)}
                >
                  {isBusy ? 'Preparing…' : 'Download'}
                </button>
              </div>

              {item.needsClass && options.class.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10 }}>
                  You aren’t timetabled to any class yet, so there is nothing to download here.
                </div>
              )}
              {errors[item.kind] && (
                <div style={{ fontSize: 12.5, color: 'var(--danger)', marginTop: 10 }}>{errors[item.kind]}</div>
              )}
              {saved[item.kind] && !errors[item.kind] && (
                <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10 }}>
                  Saved {saved[item.kind]}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
